import { connectFirehose } from '../../firehose/connect-firehose.js';
import type { LastFirehoseParams } from '../close-bridge-connection.js';
import type { AttachFirehoseContext } from './attach-firehose-context.js';
import {
  proxyPortsEqual,
  shouldReuseOpenFirehoseConnection,
} from './compare-firehose-params.js';
import { createFirehoseConnectCallbacks } from './create-firehose-connect-callbacks.js';
import {
  createFirehoseReconnectScheduler,
  logInitialFirehoseConnect,
  replaceFirehoseHandle,
} from './create-firehose-reconnect-scheduler.js';

/**
 * Open (or replace) the firehose WebSocket and wire reconnect independently of the main bridge socket.
 */
export function attachFirehoseAfterIdentified(ctx: AttachFirehoseContext, params: LastFirehoseParams): void {
  const { state, previewEnvironmentManager, logFn } = ctx;

  const prev = state.lastFirehoseParams;
  if (
    shouldReuseOpenFirehoseConnection(prev, params, () => state.firehoseHandle?.isConnected() ?? false)
  ) {
    state.lastFirehoseParams = params;
    if (prev && !proxyPortsEqual(prev.proxyPorts, params.proxyPorts)) {
      state.firehoseHandle?.updateIdentify(params);
    }
    return;
  }

  const { clearFirehoseReconnectTimer, scheduleFirehoseRetryAfterDrop } = createFirehoseReconnectScheduler(
    ctx,
    () => {
      const p = state.lastFirehoseParams;
      if (!p) return;
      attachFirehoseAfterIdentified(ctx, p);
    },
  );

  state.lastFirehoseParams = params;
  clearFirehoseReconnectTimer();
  logInitialFirehoseConnect(state, logFn);

  const myGen = replaceFirehoseHandle(state);
  const { onOpen, onClose } = createFirehoseConnectCallbacks(
    state,
    myGen,
    logFn,
    scheduleFirehoseRetryAfterDrop,
  );

  state.firehoseHandle = connectFirehose({
    firehoseServerUrl: params.firehoseServerUrl,
    workspaceId: params.workspaceId,
    bridgeName: params.bridgeName,
    proxyPorts: params.proxyPorts,
    log: logFn,
    previewEnvironmentManager,
    suppressWebSocketErrors: () => !state.closedByUser && state.firehoseOutage.startedAt != null,
    onOpen,
    onClose,
  });
}
